import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import PageHeader from '../components/PageHeader.jsx'
import Pagination from '../components/Pagination.jsx'
import ReviewPanel from '../components/ReviewPanel.jsx'
import StatusBadge from '../components/StatusBadge.jsx'
import { useAuth } from '../components/AuthProvider.jsx'
import { useInventory } from '../components/InventoryProvider.jsx'
import { useToast } from '../components/ToastProvider.jsx'
import { REVIEW_SEARCH_FIELDS, REVIEW_TABS } from '../data/constants'
import { fetchPart, fetchPartsPage } from '../services/inventoryApi'
import { canManageParts } from '../utils/permissions'
import {
  formatCurrentStock,
  getReviewStatusLabel,
  needsReview,
  partTitle,
} from '../utils/stock'

const PAGE_SIZES = [20, 50, 100]

const SUMMARY_CARDS = [
  { key: 'needed', label: '검수 필요', tab: 'needed' },
  { key: 'qty', label: '수량 미확인', tab: 'qty' },
  { key: 'spec', label: '사양 미확인', tab: 'spec' },
  { key: 'done', label: '검수완료', tab: 'done' },
]

const EMPTY_COUNTS = { needed: 0, qty: 0, spec: 0, done: 0 }

function parsePage(value) {
  const n = Number.parseInt(value, 10)
  return Number.isInteger(n) && n > 0 ? n : 1
}

function parseLimit(value) {
  const n = Number.parseInt(value, 10)
  return PAGE_SIZES.includes(n) ? n : 20
}

export default function ReviewPage() {
  const { user } = useAuth()
  const { reviewPart, deletePart } = useInventory()
  const manageParts = canManageParts(user)
  const { show } = useToast()
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState(params.get('q') || '')
  const [field, setField] = useState(params.get('field') || 'all')
  const [tab, setTab] = useState(params.get('filter') || 'needed')
  const [page, setPage] = useState(parsePage(params.get('page')))
  const [limit, setLimit] = useState(parseLimit(params.get('limit')))
  const [activeId, setActiveId] = useState(params.get('part') || '')
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [counts, setCounts] = useState(EMPTY_COUNTS)
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState('')
  const [activePart, setActivePart] = useState(null)

  function syncParams(next) {
    const merged = {
      filter: tab,
      q: query,
      field,
      page,
      limit,
      part: activeId,
      ...next,
    }
    const search = new URLSearchParams()
    if (merged.filter && merged.filter !== 'needed') search.set('filter', merged.filter)
    if (merged.q) search.set('q', merged.q)
    if (merged.field && merged.field !== 'all') search.set('field', merged.field)
    if (Number(merged.page) > 1) search.set('page', String(merged.page))
    if (Number(merged.limit) && Number(merged.limit) !== 20) search.set('limit', String(merged.limit))
    if (merged.part) search.set('part', merged.part)
    setParams(search, { replace: true })
  }

  function changeFilters(next) {
    if (next.q !== undefined) setQuery(next.q)
    if (next.field !== undefined) setField(next.field)
    if (next.filter !== undefined) setTab(next.filter)
    setPage(1)
    syncParams({ ...next, page: 1 })
  }

  function changePage(nextPage) {
    setPage(nextPage)
    syncParams({ page: nextPage })
  }

  function changeLimit(nextLimit) {
    setLimit(nextLimit)
    setPage(1)
    syncParams({ limit: nextLimit, page: 1 })
  }

  function openPart(id, extra = {}) {
    setActiveId(id)
    syncParams({ part: id, ...extra })
  }

  function closePanel() {
    setActiveId('')
    setActivePart(null)
    syncParams({ part: '' })
  }

  async function loadList({ nextPage = page, nextLimit = limit, nextQuery = query, nextField = field, nextTab = tab } = {}) {
    const result = await fetchPartsPage({
      q: nextQuery,
      field: nextField,
      verification: nextTab,
      page: nextPage,
      limit: nextLimit,
      sort: 'partCode',
      reviewCounts: 1,
    })
    setItems(result.items)
    setTotal(result.total)
    setTotalPages(result.totalPages)
    if (result.reviewCounts) setCounts({ ...EMPTY_COUNTS, ...result.reviewCounts })
    if (result.page !== nextPage) {
      setPage(result.page)
      syncParams({ page: result.page })
    }
    return result
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      setListLoading(true)
      setListError('')
      try {
        const result = await fetchPartsPage({
          q: query,
          field,
          verification: tab,
          page,
          limit,
          sort: 'partCode',
          reviewCounts: 1,
        })
        if (cancelled) return
        setItems(result.items)
        setTotal(result.total)
        setTotalPages(result.totalPages)
        if (result.reviewCounts) setCounts({ ...EMPTY_COUNTS, ...result.reviewCounts })
        if (result.page !== page) {
          setPage(result.page)
          syncParams({ page: result.page })
        }
      } catch (err) {
        if (!cancelled) setListError(err.message || '검수 목록을 불러오지 못했습니다.')
      } finally {
        if (!cancelled) setListLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [query, field, tab, page, limit])

  useEffect(() => {
    if (!activeId) {
      setActivePart(null)
      return undefined
    }
    const found = items.find((part) => part.id === activeId)
    if (found) {
      setActivePart(found)
      return undefined
    }
    let cancelled = false
    fetchPart(activeId)
      .then((part) => {
        if (!cancelled) setActivePart(part)
      })
      .catch(() => {
        if (!cancelled) setActivePart(null)
      })
    return () => {
      cancelled = true
    }
  }, [activeId, items])

  async function handleSubmit(payload) {
    const result = await reviewPart(activePart.id, payload)
    const updated = result.part
    const stillNeeded = needsReview(updated)
    if (payload.confirmQuantity || payload.confirmModel) {
      show(stillNeeded ? '검수 내용을 반영했습니다.' : '검수완료 처리되었습니다.')
    } else {
      show('검수 내용을 저장했습니다.')
    }
    return result
  }

  function nextOnPage(list, currentId, currentCode) {
    const pending = list.filter((part) => part.id !== currentId && needsReview(part))
    const index = pending.findIndex((part) => part.id === currentId)
    if (index >= 0) return pending[index + 1] || null
    return pending.find((part) => String(part.partCode || '').localeCompare(String(currentCode || ''), 'ko') > 0) || null
  }

  async function handleSaveNext(updated) {
    const currentId = updated?.id || activePart?.id
    const currentCode = updated?.partCode || activePart?.partCode || ''
    try {
      const current = await loadList({ nextPage: page })
      const localNext = nextOnPage(current.items, currentId, currentCode)
      if (localNext) {
        openPart(localNext.id)
        return
      }

      const nextPage = page + 1
      if (nextPage <= current.totalPages) {
        const following = await loadList({ nextPage })
        const firstPending = following.items.find((part) => part.id !== currentId && needsReview(part))
        if (firstPending) {
          setPage(following.page)
          openPart(firstPending.id, { page: following.page })
          return
        }
      }

      closePanel()
      show('현재 조건의 검수 대상이 모두 완료되었습니다.')
    } catch (err) {
      show(err.message || '다음 품목을 불러오지 못했습니다.')
    }
  }

  async function handleDelete(part) {
    const deleted = part || activePart
    await deletePart(deleted.id)
    show('품목이 삭제되었습니다.')
    const currentId = deleted.id
    const currentCode = deleted.partCode || ''
    try {
      const current = await loadList({ nextPage: page })
      const remaining = current.items.filter((item) => item.id !== currentId && needsReview(item))
      const localNext =
        remaining.find((item) => String(item.partCode || '').localeCompare(String(currentCode || ''), 'ko') > 0) ||
        remaining[0] ||
        null
      if (localNext) {
        openPart(localNext.id)
        return
      }

      const nextPage = page + 1
      if (nextPage <= current.totalPages) {
        const following = await loadList({ nextPage })
        const firstPending = following.items.find((item) => item.id !== currentId && needsReview(item))
        if (firstPending) {
          setPage(following.page)
          openPart(firstPending.id, { page: following.page })
          return
        }
      }

      closePanel()
    } catch (err) {
      closePanel()
      show(err.message || '검수 목록을 다시 불러오지 못했습니다.')
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="재고 검수"
        description="품목을 찾아 수량·모델·크기·보관·위치를 한 화면에서 확인하고 다음 품목으로 이동합니다."
        actions={
          <Link to="/inventory" className="btn btn--ghost btn--touch">
            재고 목록
          </Link>
        }
      />

      {listError ? <p className="form-error">{listError}</p> : null}

      <section className="stat-grid stat-grid--review" aria-label="검수 요약">
        {SUMMARY_CARDS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`stat-card stat-card--button ${tab === item.tab ? 'is-active' : ''} ${
              item.key === 'needed' ? 'stat-card--warn' : ''
            }`}
            onClick={() => changeFilters({ filter: item.tab })}
          >
            <span>{item.label}</span>
            <strong>{counts[item.key] || 0}</strong>
            <em>{item.key === 'needed' ? '종' : getReviewStatusLabel(item.key === 'done' ? 'done' : item.key)}</em>
          </button>
        ))}
      </section>

      <section className="filter-panel">
        <div className="search-row">
          <select
            className="input search-field"
            value={field}
            onChange={(e) => changeFilters({ field: e.target.value })}
            aria-label="검색 구분"
          >
            {REVIEW_SEARCH_FIELDS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <input
            className="input search-input"
            value={query}
            onChange={(e) => changeFilters({ q: e.target.value })}
            placeholder="관리번호, 모델, 품명, 제조사"
            aria-label="검색어"
          />
        </div>
        <div className="filter-toolbar">
          <div className="filter-group">
            <span className="filter-group__label">검수 상태</span>
            <div className="segmented" role="group" aria-label="검수 상태 필터">
              {REVIEW_TABS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`segmented__btn ${tab === item.value ? 'is-active' : ''}`}
                  onClick={() => changeFilters({ filter: item.value })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <p className="result-count">
        {listLoading ? '불러오는 중...' : `총 ${total.toLocaleString('ko-KR')}개 검수 대상`}
      </p>

      {items.length === 0 ? (
        <p className="empty">{listLoading ? '검수 목록을 불러오는 중입니다.' : '조건에 맞는 검수 품목이 없습니다.'}</p>
      ) : (
        <>
          <div className="card-list">
            {items.map((part) => (
              <article key={part.id} className="part-card">
                <div className="part-card__top">
                  <span className="muted">
                    {part.partCode} · {part.category}
                  </span>
                  <StatusBadge kind="verification" part={part} />
                </div>
                <strong className="model-name">{partTitle(part)}</strong>
                <span>
                  {part.manufacturer} · {part.name}
                </span>
                <div className="part-card__qty">
                  <b>{formatCurrentStock(part)}</b>
                </div>
                <div className="part-card__actions">
                  <Link to={`/inventory/${part.id}`} className="btn btn--ghost btn--touch">
                    상세
                  </Link>
                  <button type="button" className="btn btn--primary btn--touch" onClick={() => openPart(part.id)}>
                    검수
                  </button>
                </div>
              </article>
            ))}
          </div>

          <div className="table-wrap">
            <table className="data-table inventory-table">
              <thead>
                <tr>
                  <th>관리번호</th>
                  <th>분류</th>
                  <th>제조사</th>
                  <th>모델</th>
                  <th>품명</th>
                  <th>현재수량</th>
                  <th>검수상태</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((part) => (
                  <tr key={part.id} className={part.id === activeId ? 'is-active' : ''}>
                    <td>{part.partCode}</td>
                    <td>{part.category}</td>
                    <td>{part.manufacturer}</td>
                    <td className="model-name">{partTitle(part)}</td>
                    <td>{part.name}</td>
                    <td className="td-qty">{formatCurrentStock(part)}</td>
                    <td>
                      <StatusBadge kind="verification" part={part} />
                    </td>
                    <td>
                      <button type="button" className="btn btn--primary inventory-review-btn" onClick={() => openPart(part.id)}>
                        검수
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        disabled={listLoading}
        itemLabel="개 검수 대상"
        onPageChange={changePage}
        onLimitChange={changeLimit}
      />

      <ReviewPanel
        part={activePart}
        open={Boolean(activePart)}
        onClose={closePanel}
        onSubmit={handleSubmit}
        onSaveNext={handleSaveNext}
        canDelete={manageParts}
        onDelete={handleDelete}
      />
    </div>
  )
}
